/**
 * Adds a member from the Admin CRM.
 *
 * Renders the same field components the public signup renders — `Field`,
 * `TextInput`, `SelectInput`, `InterestTypeahead`, `DirectoryPrivacyControls` — and
 * writes through the same server function, so an admin-created member and a
 * self-registered one are the same record. If a field changes for signup it changes
 * here, because it is literally the same component.
 *
 * Two deliberate differences from signup, both because an administrator is entering
 * somebody else's details rather than their own:
 *
 * Only a name and email are required. Signup insists on company, title, industry and
 * an interest because the member is describing themselves and will benefit from the
 * directory. An administrator typing in somebody they met has a business card, not an
 * interview, and blocking on fields they do not have produces invented data.
 *
 * Directory consent starts entirely off. The new-account defaults publish every
 * field, which is a reasonable thing for a person to choose for themselves and not a
 * thing an administrator may choose on their behalf.
 */
import { useEffect, useState } from "react";
import { Loader2, Mail, User, X } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { PhoneInput, LinkedInInput, draftPhoneValue, draftLinkedInValue, storedPhoneValue, storedLinkedInValue } from "@/components/ProfileContactInputs";
import { Field, SelectInput, TextInput, TextareaInput } from "@/components/memberForm/fields";
import DirectoryPrivacyControls from "@/components/DirectoryPrivacyControls";
import InterestTypeahead from "@/components/InterestTypeahead";
import { MEMBER_INTERESTS } from "@shared/memberInterests";
import { ADMIN_ADDED_ACCOUNT_DIRECTORY_PRIVACY, type DirectoryPrivacyPreferences } from "@shared/directoryPrivacy";

const INDUSTRIES = [
  "Consulting & Professional Services",
  "Financial Services",
  "Healthcare",
  "Technology",
  "Manufacturing",
  "Retail & Consumer",
  "Education",
  "Nonprofit",
  "Transportation & Logistics",
  "Other",
];

/**
 * Nothing published until the member says so. See the note above.
 *
 * The shared constant, not a copy of it: the bulk event-enrollment path creates
 * accounts on the same terms, and two hand-maintained lists of eleven flags
 * agree only until one is edited.
 */
const ADMIN_ENTERED_PRIVACY: DirectoryPrivacyPreferences = { ...ADMIN_ADDED_ACCOUNT_DIRECTORY_PRIVACY };

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  phoneCountryCode: "+1",
  company: "",
  jobTitle: "",
  industry: "",
  linkedinUrl: "",
  bio: "",
  interests: [] as string[],
  grantMembership: false,
};

export default function AddMemberDrawer({ onClose, onCreated }: { onClose: () => void; onCreated?: () => void }) {
  const [form, setForm] = useState(emptyForm);
  const [privacy, setPrivacy] = useState<DirectoryPrivacyPreferences>({ ...ADMIN_ENTERED_PRIVACY });
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const utils = trpc.useUtils();

  const createMember = trpc.members.createMemberAccount.useMutation({
    onSuccess: async created => {
      await utils.members.adminRecords.invalidate();
      chiNotify.success("Member added", { description: `${created.fullName} can now sign in with ${created.email}.` });
      onCreated?.();
      onClose();
    },
    onError: mutationError => setError(mutationError.message),
  });

  useEffect(() => {
    setVisible(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !createMember.isPending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [createMember.isPending, onClose]);

  const set = (key: keyof typeof emptyForm) => (value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const canSubmit = form.fullName.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!canSubmit || createMember.isPending) return;

    /*
     * The strict converters, reported rather than thrown.
     *
     * Submitting is the moment an incomplete phone number or an unusable
     * LinkedIn username genuinely is an error — but an uncaught throw here takes
     * the drawer down and loses everything typed into it. Caught, it becomes the
     * message already rendered above the buttons.
     */
    let phone: string | undefined;
    let linkedinUrl: string | undefined;
    try {
      phone = storedPhoneValue(form.phoneCountryCode, form.phone);
      linkedinUrl = storedLinkedInValue(form.linkedinUrl);
    } catch (conversionError) {
      setError(conversionError instanceof Error ? conversionError.message : "Check the phone number and LinkedIn username.");
      return;
    }

    createMember.mutate({
      fullName: form.fullName,
      email: form.email,
      phone,
      company: form.company || undefined,
      jobTitle: form.jobTitle || undefined,
      industry: form.industry || undefined,
      linkedinUrl,
      bio: form.bio || undefined,
      interests: form.interests,
      directoryPrivacy: privacy,
      openToWork: false,
      accessState: form.grantMembership ? "active" : undefined,
    });
  };

  return (
    <>
      <div
        role="presentation"
        onClick={() => { if (!createMember.isPending) onClose(); }}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          zIndex: 999, opacity: visible ? 1 : 0, transition: "opacity 0.3s ease",
        }}
      />

      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-member-title"
        aria-busy={createMember.isPending}
        onSubmit={submit}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(480px, 100vw)", background: "#0D1220", zIndex: 1000,
          borderLeft: "1px solid rgba(233,30,140,.28)",
          boxShadow: "-24px 0 60px rgba(0,0,0,.5)",
          display: "flex", flexDirection: "column",
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(.23,1,.32,1)",
        }}
      >
        <header style={{ display: "flex", alignItems: "flex-start", gap: ".8rem", padding: "1.25rem 1.25rem 1rem", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: "0 0 .25rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>Members</p>
            <h2 id="add-member-title" style={{ margin: 0, color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.2rem", letterSpacing: "-.02em" }}>Add a member</h2>
            <p style={{ margin: ".35rem 0 0", color: "#8390A6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
              Creates the account directly. No sign-in code is sent — they can sign in whenever they like.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close without adding a member"
            onClick={onClose}
            disabled={createMember.isPending}
            style={{ marginLeft: "auto", width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".65rem", background: "rgba(255,255,255,.025)", color: "#AEB9C9", cursor: createMember.isPending ? "default" : "pointer" }}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
          <Field label="Full Name" required>
            <TextInput value={form.fullName} onChange={set("fullName")} placeholder="Enter your full name" icon={User} required />
          </Field>

          <Field label="Email Address" required>
            <TextInput value={form.email} onChange={set("email")} placeholder="Enter your email address" type="email" icon={Mail} required />
          </Field>

          <Field label="Phone" hint="optional">
            {/* Same control as create-account: formats as you type, stores E.164. */}
            <PhoneInput
              countryCode={form.phoneCountryCode}
              value={form.phone}
              onChange={next => setForm(current => ({ ...current, phoneCountryCode: next.countryCode, phone: next.value }))}
            />
          </Field>

          <Field label="Company / Organization" hint="optional">
            <TextInput value={form.company} onChange={set("company")} placeholder="Enter your company or organization" />
          </Field>

          <Field label="Job Title" hint="optional">
            <TextInput value={form.jobTitle} onChange={set("jobTitle")} placeholder="Enter your job title" />
          </Field>

          <Field label="Industry" hint="optional">
            <SelectInput value={form.industry} onChange={set("industry")} options={INDUSTRIES} placeholder="Select an industry…" />
          </Field>

          <Field label="LinkedIn" hint="optional">
            {/* The username after a fixed prefix, not a free-text URL. */}
            <LinkedInInput
              value={form.linkedinUrl}
              onChange={username => setForm(current => ({ ...current, linkedinUrl: username }))}
            />
          </Field>

          <Field label="Bio" hint="optional">
            <TextareaInput value={form.bio} onChange={set("bio")} placeholder="Enter a short professional introduction" rows={4} />
          </Field>

          <Field label="Areas of Interest" hint="optional">
            <InterestTypeahead
              options={MEMBER_INTERESTS}
              selected={form.interests}
              onToggle={interest =>
                setForm(prev => ({
                  ...prev,
                  interests: prev.interests.includes(interest)
                    ? prev.interests.filter(entry => entry !== interest)
                    : [...prev.interests, interest],
                }))
              }
            />
          </Field>

          <div style={{ margin: "1.25rem 0", padding: ".85rem", borderRadius: ".7rem", border: "1px solid rgba(245,166,35,.24)", background: "rgba(245,166,35,.06)" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.grantMembership}
                onChange={event => setForm(prev => ({ ...prev, grantMembership: event.target.checked }))}
                style={{ marginTop: ".2rem", width: 18, height: 18, flexShrink: 0, cursor: "pointer" }}
              />
              <span>
                <span style={{ display: "block", color: "#F5C06A", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>Grant an active membership</span>
                <span style={{ display: "block", marginTop: ".2rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
                  For somebody who paid outside Stripe. No charge is made and Stripe will not show a subscription for them.
                </span>
              </span>
            </label>
          </div>

          {/*
            The same control the member sees on their own profile, starting fully off.
            Publishing somebody's details is their decision, not an administrator's.
          */}
          <DirectoryPrivacyControls
            value={privacy}
            onChange={setPrivacy}
            preview={{
              name: form.fullName || "New member",
              title: form.jobTitle || undefined,
              company: form.company || undefined,
              industry: form.industry || undefined,
              interests: form.interests,
              bio: form.bio || undefined,
              email: form.email || undefined,
              /* Draft converters: this preview re-renders on every keystroke,
                 and the throwing versions took the drawer down on a half-typed
                 number rather than simply showing nothing yet. */
              phone: draftPhoneValue(form.phoneCountryCode, form.phone),
              linkedinUrl: draftLinkedInValue(form.linkedinUrl),
            }}
          />

          {error && (
            <p role="alert" style={{ margin: "1rem 0 0", padding: ".7rem .8rem", borderRadius: ".6rem", border: "1px solid rgba(248,113,113,.32)", background: "rgba(248,113,113,.08)", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
              {error}
            </p>
          )}
        </div>

        <footer style={{ display: "flex", justifyContent: "flex-end", gap: ".6rem", padding: "1rem 1.25rem", borderTop: "1px solid rgba(255,255,255,.08)" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={createMember.isPending}
            style={{ minHeight: 44, padding: "0 1rem", border: "1px solid rgba(255,255,255,.14)", borderRadius: ".55rem", background: "transparent", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: createMember.isPending ? "default" : "pointer" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || createMember.isPending}
            style={{ minHeight: 44, display: "inline-flex", alignItems: "center", gap: ".45rem", padding: "0 1.1rem", border: 0, borderRadius: ".55rem", background: canSubmit ? "linear-gradient(135deg,#E91E8C,#C2185B)" : "rgba(255,255,255,.06)", color: canSubmit ? "white" : "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: !canSubmit || createMember.isPending ? "default" : "pointer" }}
          >
            {createMember.isPending ? <><Loader2 size={15} className="animate-spin" /> Adding…</> : "Add member"}
          </button>
        </footer>
      </form>
    </>
  );
}
